<script setup lang="ts">
import type {
	ProductContentsResponseSchema,
	ProductSchema,
} from '@laam/cms-shared';
import { VideoType } from '@laam/ui/base';
import { toRefs, useThrottleFn } from '@vueuse/core';
import Hls from 'hls.js';
import { inject, onMounted, onUnmounted, ref, watch } from 'vue';
import {
	type VideosInteractionMetaData,
	type VideosMetaData,
} from '../../../../cathode/src/events/index.ts';
import { isMobileView } from '../../../utils/helpers.ts';
import { type VideoPauseEvent, type VideoPlayEvent } from './types';

interface Props {
	videoUrl: string;
	src: string;
	isPlaying: boolean;
	poster?: string;
	videoType?: VideoType;
	productId?: number;
}
const props = defineProps<Props>();
const { videoUrl } = toRefs(props);
const emit = defineEmits<{
	(e: 'cathode:pauseEvent', payload: VideosMetaData): void;
	(e: 'play', payload: VideoPlayEvent): void;
	(e: 'interaction', payload: VideosInteractionMetaData): void;
	(e: 'pause', payload: VideoPauseEvent): void;
}>();

type VideoEventEmitter<T> = (event: { payload: T }) => void;

const emitVideoPlay = inject<VideoEventEmitter<VideoPlayEvent>>('video-play');
const emitVideoPause =
	inject<VideoEventEmitter<VideoPauseEvent>>('video-pause');
const emitCathodePause = inject<VideoEventEmitter<VideosMetaData>>(
	'cathode-video-pause',
);
const emitInteraction = inject<VideoEventEmitter<VideosInteractionMetaData>>(
	'cathode-video-interaction',
);

const video = ref<HTMLVideoElement | null>(null);
let hls: Hls | null = null;
const totalWatchTime = ref(0);
const lastTime = ref(0);
let bufferingTime: any = null;

const videoInteractions = [
	'waiting',
	'volumechange',
	'seeked',
	'fullscreenchange',
	'play',
	'pause',
] as const;

const videoEventHandlers = new Map();

const productData = inject('productData') as ProductSchema;
const contentData = inject(
	'contentData',
) as ProductContentsResponseSchema['contents'][number];
const storeID = inject('storeID') as number;
const carouselType = inject<string>('carousel-type');
const parentPage = inject<string>('parent');

const preparePlayer = () => {
	if (hls) hls.destroy();
	hls = new Hls();
	hls.loadSource(props.src);
	if (video.value) hls.attachMedia(video.value);
};

onMounted(() => {
	props.videoType === VideoType.HLS && preparePlayer();
	//TODO: add cms condition to all conditional code

	if (video.value) {
		video.value.addEventListener('play', onPlay);
		video.value.addEventListener('pause', onEnded);
		video.value.addEventListener('timeupdate', onTimeUpdate);
		video.value.addEventListener('waiting', onWaiting);
		video.value.addEventListener('playing', onResumeFromBuffering);

		videoInteractions.forEach((interaction) => {
			const handler = ['seeked', 'volumechange', 'fullscreenchange'].includes(
				interaction,
			)
				? (event: Event) => sendThrottledEvents(event.type)
				: (event: Event) => onVideoInteraction(event.type);
			video.value!.addEventListener(interaction, handler);
			videoEventHandlers.set(interaction, handler);
		});
	}
});
onUnmounted(() => {
	if (video.value) {
		videoEventHandlers.forEach((handler, interaction) => {
			video.value!.removeEventListener(interaction, handler);
		});
	}
	hls?.destroy();
});

const onPlay = () => {
	lastTime.value = video.value?.currentTime || 0;

	if (video.value) {
		const playPayload: VideoPlayEvent = {
			productId: productData?.shopify_id || contentData.product_shopify_id || 0,
			videoUrl: videoUrl.value,
			totalDuration: video.value.duration,
			stockStatus: productData?.stock_status || contentData.stock_status,
			carouselType: carouselType,
			source: window.location.href,
		};
		emit('play', playPayload);

		emitVideoPlay?.({
			payload: playPayload,
		});
	}
};

const onTimeUpdate = () => {
	if (!video.value) return;
	const currentTime = video.value.currentTime;
	const delta = currentTime - lastTime.value;
	if (delta > 0) {
		totalWatchTime.value += delta;
		lastTime.value = currentTime;
	}
};

const onWaiting = () => {
	if (bufferingTime === null) {
		bufferingTime = performance.now();
	}
};
const onResumeFromBuffering = () => {
	if (bufferingTime !== null) {
		const waitingDuration = (
			(performance.now() - bufferingTime) /
			1000
		).toFixed(3);
		bufferingTime = null;
		onVideoInteraction('waiting', waitingDuration);
	}
};

const onEnded = () => {
	if (video.value) {
		const pausePayload: VideoPauseEvent = {
			watchedTime: totalWatchTime.value.toFixed(3),
			totalDuration: video.value.duration,
			no_of_times_played: video.value.duration
				? Number((totalWatchTime.value / video.value.duration).toFixed(3))
				: 0,
			productId: productData?.shopify_id || contentData.product_shopify_id || 0,
			videoUrl: videoUrl.value,
			stockStatus: productData?.stock_status || contentData.stock_status,
			carouselType: carouselType,
			source: window.location.href,
		};

		const cathodePausePayload: VideosMetaData = {
			component: carouselType,
			source: window.location.href,
			page: parentPage,
			store_id: storeID,
			data: [
				{
					no_of_times: video.value.duration
						? Math.floor(totalWatchTime.value / video.value.duration)
						: 0,
					total_duration: video.value.duration.toFixed(3),
					watched_duration: totalWatchTime.value.toFixed(3),
					video_url: videoUrl.value,
					id: productData?.shopify_id || contentData.product_shopify_id || 0,
					inventory:
						productData?.total_inventory_quantity ||
						contentData.total_inventory_quantity,
					content_id: productData?.content_videos
						? productData?.content_videos[0]?.content_id
						: contentData.content_id,
				},
			],
		};

		emitVideoPause?.({
			payload: pausePayload,
		});

		emitCathodePause?.({
			payload: cathodePausePayload,
		});
	}
};

const togglePlay = async () => {
	if (!video.value || isMobileView()) return;
	try {
		if (video?.value?.paused) {
			await video.value.play();
		} else {
			video?.value?.pause();
		}
	} catch (e) {
		console.error('Playback error:', e);
	}
};

const onVideoInteraction = (
	interactionType: string,
	bufferingTime?: string,
) => {
	if (video.value) {
		const interactionPayload: VideosInteractionMetaData = {
			component: carouselType,
			source: window.location.href,
			data_source: '',
			page: parentPage,
			store_id: storeID,
			data: [
				{
					video_url: videoUrl.value ?? '',
					product_id:
						(productData?.shopify_id || contentData.product_shopify_id) ?? 0,
					interaction_type: interactionType,
					waiting_time_period: bufferingTime ?? '0',
					content_id: productData?.content_videos
						? productData?.content_videos[0]?.content_id
						: contentData.content_id,
				},
			],
		};

		emitInteraction?.({
			payload: interactionPayload,
		});
	}
};

const sendThrottledEvents = useThrottleFn(onVideoInteraction, 1000);
watch(
	() => props.isPlaying,
	async (isPlaying) => {
		try {
			if (isPlaying) {
				await video.value?.play();
			} else {
				video.value?.pause();
			}
		} catch (e) {
			console.log('Error while playing video', e);
		}
	},
);
</script>

<template>
	<video
		ref="video"
		class="size-full object-cover"
		autoplay
		muted
		controls
		disablePictureInPicture
		loop
		playsinline
		webkit-playsinline
		controlslist="nodownload noremoteplayback noplaybackrate nofullscreen"
		:poster
		:src
		preload="metadata"
		@click="isMobileView() ? togglePlay : null"
	></video>
</template>
